import React from 'react';
import { Clock, AlertTriangle, CheckCircle } from 'lucide-react';
import { Badge } from './../ui/badge';
import { differenceInHours, differenceInMinutes } from 'date-fns';

export default function TimeProgress({ task }) {
  if (!task.estimated_hours || !task.started_at || task.status === 'backlog' || task.status === 'todo') {
    return null;
  }

  const startedAt = new Date(task.started_at);
  const now = new Date();
  
  // Calculate elapsed time
  const elapsedMinutes = differenceInMinutes(now, startedAt);
  const elapsedHours = elapsedMinutes / 60;
  
  const estimatedHours = task.estimated_hours;
  const percentageUsed = (elapsedHours / estimatedHours) * 100;
  
  // Determine status
  const isCompleted = task.status === 'done';
  const isOvertime = elapsedHours > estimatedHours && !isCompleted;
  const isNearDeadline = percentageUsed > 80 && percentageUsed <= 100 && !isCompleted;
  
  // Format elapsed time
  const formatTime = (hours) => {
    if (hours < 1) {
      return `${Math.round(hours * 60)}min`;
    }
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return m > 0 ? `${h}h ${m}min` : `${h}h`;
  };

  // Color scheme
  let barColor = 'from-blue-500 to-cyan-500';
  let statusColor = 'bg-blue-100 text-blue-700';
  let icon = <Clock className="w-3 h-3" />;
  
  if (isCompleted) {
    barColor = 'from-emerald-500 to-teal-500';
    statusColor = 'bg-emerald-100 text-emerald-700';
    icon = <CheckCircle className="w-3 h-3" />;
  } else if (isOvertime) {
    barColor = 'from-red-500 to-rose-500';
    statusColor = 'bg-red-100 text-red-700';
    icon = <AlertTriangle className="w-3 h-3" />;
  } else if (isNearDeadline) {
    barColor = 'from-amber-500 to-orange-500';
    statusColor = 'bg-amber-100 text-amber-700';
    icon = <AlertTriangle className="w-3 h-3" />;
  }

  return (
    <div className="space-y-2">
      {/* Time Info */}
      <div className="flex items-center justify-between text-xs">
        <span className="text-slate-600 font-medium">Tempo Estimado</span>
        <Badge className={`${statusColor} font-semibold`}>
          {icon}
          <span className="ml-1">
            {formatTime(elapsedHours)} / {estimatedHours}h
          </span>
        </Badge>
      </div>

      {/* Progress Bar */}
      <div className="relative h-3 bg-slate-100 rounded-full overflow-hidden">
        <div
          className={`h-full bg-gradient-to-r ${barColor} transition-all duration-500`}
          style={{ width: `${Math.min(percentageUsed, 100)}%` }}
        />
        {isOvertime && (
          <div
            className="absolute top-0 h-full bg-gradient-to-r from-red-500/20 to-rose-500/20"
            style={{ left: '100%', width: `${percentageUsed - 100}%` }}
          />
        )}
      </div>

      {/* Status Message */}
      {isOvertime && (
        <p className="text-xs text-red-600 font-medium">
          ⚠️ {formatTime(elapsedHours - estimatedHours)} acima do estimado
        </p>
      )}
      {isNearDeadline && !isOvertime && (
        <p className="text-xs text-amber-600 font-medium">
          ⏰ Próximo do tempo limite
        </p>
      )}
      {isCompleted && elapsedHours <= estimatedHours && (
        <p className="text-xs text-emerald-600 font-medium">
          ✓ Concluído dentro do prazo
        </p>
      )}
    </div>
  );
}